import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Doughnut, Bar, getElementAtEvent } from "react-chartjs-2";
import { Button, DatePicker, Select, message } from "antd";
import AdminJewelryReportService from "../../../services/admin-jewelryReport-service";
import { bucketTopN, toShareRows } from "../../ChartsGroup/DiamondReportsCharts/_shared/bucketTopN";
import CustomLegend from "../../ChartsGroup/DiamondReportsCharts/_shared/CustomLegend";
import ChartState from "../../ChartsGroup/DiamondReportsCharts/_shared/ChartState";
import { donutOptions, barOptions, donutPctOptions, barPctOptions } from "../../ChartsGroup/DiamondReportsCharts/_shared/chartTheme";
import { exportChartPdf } from "../../ChartsGroup/DiamondReportsCharts/_shared/exportChartPdf";
import { trendFor, pctTotalKpi } from "../../ChartsGroup/DiamondReportsCharts/_shared/kpiHelpers";
import JewelryDetailSearchTable from "../jewelry/_shared/JewelryDetailSearchTable";
import { extractDealerIds, fetchPriorPeriodTotalClicks } from "../jewelry/_shared/jewelryHelpers";
import { BlobToDownloadFile } from "../../../helper/commanFunction";
import { isValidMetalType } from "../jewelry/_shared/isValidMetalType";
import { chartColors } from "../colors";
import { GLOBAL_REPORT_PERCENT_ONLY, GLOBAL_REPORT_PRIOR_LAST_YEAR } from "../_shared/globalReportSettings";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Global combined flagship. Bug 2071 (reopened): the "Jewelry Clicks Based
// on Vendor/Retailer" bar-chart panels were missing here even though the
// backend-rendered PDF for this same page already included them (same
// Admin_JewelryTopVendorRetailerChartColunm/Admin_JewelryTopRetailerChartColunm
// calls, reporttype "Global") -- wired up to match, same panel layout as
// JewelryChartReportAdmin.js. The Vendor(s)/Retailer(s) FILTER dropdowns
// Bug 1840 removed from Global scope stay removed (browsing the full
// vendor/retailer directory is a different exposure than showing the
// performance charts themselves) -- left commented out below, untouched.
const bandStart = (label) => {
  if (!label || label === "Call For Price") return Infinity;
  const match = String(label).replace(/,/g, "").match(/[\d.]+/);
  return match ? Number(match[0]) : Infinity;
};

const parsePriceBand = (label) => {
  if (label === "Call For Price") return { price: "Call For Price", price1: "", price2: "" };
  const [low, high] = label.split("-");
  const lowPrice1 = parseInt(low.replace(/^.*?\$/, "").replace(/,/g, "").trim(), 10);
  const highPrice2 = parseInt((high || "").replace(/[$,]/g, ""), 10);
  // Backend's Price1/Price2 fields are typed string -- a bare number here
  // fails model binding (400) and the table never updates.
  return {
    price: "",
    price1: low.includes("Under") ? "0" : String(lowPrice1),
    price2: low.includes("Under") ? String(lowPrice1) : (highPrice2 ? String(highPrice2) : ""),
  };
};

const emptyFilter = { category: "", vendor: "", retailer: "", price: "", price1: "", price2: "", metalType: "", metalColorType: "" };

const GlobalJewelryChartReports = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);
  const [dealerId, setDealerId] = useState();
  const [forDealer, setForDealer] = useState("");
  const [dealerMemberTypeName, setDealerMemberTypeName] = useState("");
  const [dataRangeFrom, setDataRangeFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dataRangeTo, setDataRangeTo] = useState(moment().format("YYYY-MM-DD"));
  const [vendorIds, setVendorIds] = useState([]);
  const [retailerIds, setRetailerIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [retailerList, setRetailerList] = useState([]);

  const [categoryData, setCategoryData] = useState([]);
  const [priceData, setPriceData] = useState([]);
  const [vendorClicksData, setVendorClicksData] = useState([]);
  const [retailerClicksData, setRetailerClicksData] = useState([]);
  const [metalData, setMetalData] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [totalClicks, setTotalClicks] = useState(0);
  const [priorTotalClicks, setPriorTotalClicks] = useState(null);

  const [filter, setFilter] = useState(emptyFilter);
  const [refetchToken, setRefetchToken] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activityPdfLoading, setActivityPdfLoading] = useState(false);

  const categoryChartRef = useRef();
  const priceChartRef = useRef();
  const vendorChartRef = useRef();
  const retailerChartRef = useRef();
  const metalChartRef = useRef();

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      setDealerMemberTypeName(loginDetials.loginDetials.responseData.dealerMemberTypeName);
      handleGetForDealer(id);
    }
    handleGetVendorDropdownData();
    handleGetRetailerDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetForDealer = (id) => {
    AdminJewelryReportService.GetForDealer({ dealerID: id }).then((response) => {
      if (response.data.message === "Success") {
        const fd = response.data.responseData.forDealer;
        setForDealer(fd);
        handleLoadAllCharts(id, fd, dataRangeFrom, dataRangeTo, [], []);
      }
    });
  };

  const handleGetVendorDropdownData = () => {
    const id = loginDetials?.loginDetials?.responseData?.dealerId;
    AdminJewelryReportService.GetVendorDropdownList({ dealerId: id, reporttype: "global" }).then((response) => {
      if (response.data.message === "Success") setVendorList(response.data.responseData);
    });
  };

  const handleGetRetailerDropdownData = () => {
    AdminJewelryReportService.GetRetailerDropdownList({ dealerId: 0, reporttype: "global" }).then((response) => {
      if (response.data.message === "Success") setRetailerList(response.data.responseData);
    });
  };

  const handleVendorChange = (values) => {
    setVendorIds(values);
    if (values.length === 0) {
      handleGetRetailerDropdownData();
    } else {
      AdminJewelryReportService.ChangeRetailerList({ vendorIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setRetailerList(response.data.responseData);
      });
    }
  };

  const handleRetailerChange = (values) => {
    setRetailerIds(values);
    if (values.length === 0) {
      handleGetVendorDropdownData();
    } else {
      AdminJewelryReportService.ChangeVendorList({ retailerIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setVendorList(response.data.responseData);
      });
    }
  };

  const handleLoadAllCharts = (dealerIdArg, forDealerArg, fromDate, toDate, vIds, rIds) => {
    setChartsLoading(true);
    const base = {
      dealerID: dealerIdArg,
      forDealer: forDealerArg,
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(rIds).toString(),
      vendorIds: extractDealerIds(vIds).toString(),
    };
    Promise.allSettled([
      AdminJewelryReportService.GetClicksBasedOnCategory(base),
      AdminJewelryReportService.GetClicksBasedOnPrice(base),
      AdminJewelryReportService.GetClicksBasedOnVendor({ ...base, reporttype: "Global" }),
      AdminJewelryReportService.GetClicksBasedOnRetailer({ ...base, reporttype: "Global" }),
      AdminJewelryReportService.GetMetalTypeData(base),
    ]).then(([categoryRes, priceRes, vendorRes, retailerRes, metalRes]) => {
      const okData = (settled) => {
        if (settled.status !== "fulfilled") {
          console.error("Global jewelry chart fetch failed:", settled.reason);
          return [];
        }
        return settled.value.data.message === "Success" ? settled.value.data.responseData || [] : [];
      };
      const category = okData(categoryRes);
      const price = okData(priceRes);
      const vendor = okData(vendorRes);
      const retailer = okData(retailerRes);
      setCategoryData(category);
      setPriceData(price);
      setVendorClicksData(vendor);
      setRetailerClicksData(retailer);
      setMetalData(okData(metalRes));
      setTotalClicks(category.reduce((sum, row) => sum + Number(row.count || 0), 0));
      setChartsLoading(false);
    });

    setPriorTotalClicks(null);
    fetchPriorPeriodTotalClicks(
      AdminJewelryReportService.GetClicksBasedOnCategory,
      (pFrom, pTo) => ({ ...base, fromDate: pFrom, toDate: pTo }),
      fromDate,
      toDate,
      "count",
      (data) => data,
      GLOBAL_REPORT_PRIOR_LAST_YEAR
    ).then(setPriorTotalClicks);
  };

  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      setDataRangeFrom(dateStrings[0]);
      setDataRangeTo(dateStrings[1]);
    }
  };

  const handleApplyFilter = () => {
    setFilter(emptyFilter);
    handleLoadAllCharts(dealerId, forDealer, dataRangeFrom, dataRangeTo, vendorIds, retailerIds);
    setRefetchToken((tok) => tok + 1);
  };

  const handleCategoryClick = (category) => setFilter({ ...emptyFilter, category });
  const handleVendorClick = (vendor) => setFilter({ ...emptyFilter, vendor });
  const handleRetailerClick = (retailer) => setFilter({ ...emptyFilter, retailer });
  const handlePriceClick = (label) => setFilter({ ...emptyFilter, ...parsePriceBand(label) });
  // The table SP filters on the compound metalTypeID + metalColorID carried on
  // each chart row (same as the standalone Global Metal Type report).
  const handleMetalClick = (label) => {
    const row = validMetalData.find((item) => item.metalType === label);
    if (row) setFilter({ ...emptyFilter, metalType: String(row.metalTypeID ?? ""), metalColorType: String(row.metalColorID ?? "") });
  };

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    const vendorNames = vendorIds.length === 0 ? t("All Vendors") : vendorList.filter((v) => extractDealerIds(vendorIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    const retailerNames = retailerIds.length === 0 ? t("All Retailers") : retailerList.filter((v) => extractDealerIds(retailerIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: t("Jewelry Chart Report"),
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom,
        dataRangeTo,
        infoLines: [
          ...(dealerMemberTypeName !== "Vendor" ? [`Vendors: ${vendorNames}`] : []),
          ...(dealerMemberTypeName === "Admin" ? [`Retailers: ${retailerNames}`] : []),
          // Percent-only: no click count line (the share of the total would always be 100%).
          ...(GLOBAL_REPORT_PERCENT_ONLY ? [] : [`Total Clicks: ${totalClicks}`]),
        ],
        fileName: "Global-Jewelry-Chart-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // Download PDF - the full, server-rendered Activity Report (KPIs + all
  // charts + notable-views table), not a single-chart snapshot like
  // handleDownloadChart above. Same filters already on screen (same shape
  // as handleLoadAllCharts' "base" object) so it matches what's charted.
  // See readMe/02_PLATFORM_PDF_EXPORT_IMPLEMENTATION_PLAN.md.
  const handleDownloadActivityPdf = () => {
    let inputData = {
      dealerID: dealerId ? dealerId.toString() : "",
      forDealer: forDealer ? forDealer.toString() : "",
      fromDate: moment(dataRangeFrom).format("YYYY-MM-DD"),
      toDate: moment(dataRangeTo).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(retailerIds).toString(),
      vendorIds: extractDealerIds(vendorIds).toString(),
      showMetalChart: true,
      percentOnly: GLOBAL_REPORT_PERCENT_ONLY,
      priorLastYear: GLOBAL_REPORT_PRIOR_LAST_YEAR,
    };
    setActivityPdfLoading(true);
    AdminJewelryReportService.ExportActivityPdf(inputData)
      .then((response) => {
        setActivityPdfLoading(false);
        const fileName = `Jewelry_Activity_Global_${dataRangeFrom}_${dataRangeTo}.pdf`;
        BlobToDownloadFile(response.data, fileName, "pdf");
      })
      .catch(() => {
        setActivityPdfLoading(false);
        message.error(t("Failed to generate PDF. Please try again."));
      });
  };

  const categoryChart = useMemo(
    () => bucketTopN(categoryData, { labelKey: "category", valueKey: "count", chartLabel: "Category", othersLabel: t("Others") }),
    [categoryData, t]
  );
  const vendorChart = useMemo(
    () => bucketTopN(vendorClicksData, { labelKey: "dealerCompany", valueKey: "total", chartLabel: "Vendor", othersLabel: t("Others") }),
    [vendorClicksData, t]
  );
  const retailerChart = useMemo(
    () => bucketTopN(retailerClicksData, { labelKey: "dealerCompany", valueKey: "total", chartLabel: "Retailer", othersLabel: t("Others") }),
    [retailerClicksData, t]
  );
  const validMetalData = useMemo(() => metalData.filter((row) => isValidMetalType(row.metalType)), [metalData]);
  const metalChart = useMemo(
    () => bucketTopN(validMetalData, { labelKey: "metalType", valueKey: "count", chartLabel: "Metal Type", othersLabel: t("Others") }),
    [validMetalData, t]
  );
  const priceRows = useMemo(() => {
    const list = priceData.filter((item) => Number(item.total) > 0);
    const sorted = [...list].sort((a, b) => bandStart(a.range) - bandStart(b.range));
    const total = sorted.reduce((sum, item) => sum + Number(item.total), 0);
    return sorted.map((item) => ({
      label: item.range,
      value: Number(item.total),
      percentage: total > 0 ? ((Number(item.total) / total) * 100).toFixed(2) : 0,
    }));
  }, [priceData]);
  const priceChartData = [["Price Band", "Count"], ...priceRows.map((r) => [`${r.label} (${r.percentage}%)`, r.value])];

  const makeClickHandler = (ref, rows, onPick) => (event) => {
    if (!ref.current) return;
    const [el] = getElementAtEvent(ref.current, event);
    if (!el) return;
    const label = rows[el.index]?.[0]?.replace(/\s*\([^)]*\)\s*$/, "");
    if (label && label !== t("Others")) onPick(label);
  };

  const topCategory = [...categoryData].sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
  const topPriceBand = [...priceRows].sort((a, b) => b.value - a.value)[0];
  const topVendor = [...vendorClicksData].sort((a, b) => Number(b.total || 0) - Number(a.total || 0))[0];
  const topRetailer = [...retailerClicksData].sort((a, b) => Number(b.total || 0) - Number(a.total || 0))[0];
  const topMetal = [...validMetalData].sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
  const metalTotal = validMetalData.reduce((sum, row) => sum + Number(row.count || 0), 0);
  // Task 2344: Global Jewelry shows shares only, never raw click counts.
  const shareOf = (value, list, key) => {
    const total = list.reduce((sum, row) => sum + Number(row[key] || 0), 0);
    return total > 0 ? ((Number(value || 0) / total) * 100).toFixed(2) : "0.00";
  };
  const clicksTrend = priorTotalClicks != null ? trendFor([{ Metric: "TotalClicks", CurrentValue: totalClicks, PriorValue: priorTotalClicks }], "TotalClicks") : null;
  const totalPct = pctTotalKpi(totalClicks, priorTotalClicks);

  return (
    <div className="pendjewelreq__section form__fields">
      <div className="row magento__mapping">
        <div className="col-md-12">
          <div className="edr-pagehead-row">
            <div className="edr-pagehead">
              <h1 className="edr-pagehead-title">{t("Jewelry Chart Report")}</h1>
              <div className="edr-pagehead-sub">
                {moment(dataRangeFrom).format("MMM D, YYYY")} – {moment(dataRangeTo).format("MMM D, YYYY")}
              </div>
            </div>
            <div className="edr-pagehead-actions">
              {/* "Download Chart" (client-side screenshot) hidden - the real
                  backend-rendered "Download PDF" below covers this page
                  already (Task: hide Download Chart platform-wide, 2026-09-09). */}
              <Button className="ghost-btn" onClick={handleDownloadActivityPdf} loading={activityPdfLoading}>
                <i className="fa fa-download"></i> {t("Download PDF")}
              </Button>
              <button className="primary-btn" onClick={handleApplyFilter}>{t("Apply Filter")}</button>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <div className="row edr-filters">
            <div className="col-md-3">
              <div className="input__block dataRange-input-block datepicker__block">
                <label>{t("Date Range")}</label>
                <RangePicker
                  className="date-range-input"
                  ranges={{
                    Today: [moment(), moment()],
                    Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
                    "Last 7 Days": [moment().subtract(7, "days"), moment()],
                    "Last 30 Days": [moment().subtract(30, "days"), moment()],
                    "This Month": [moment().startOf("month"), moment().endOf("month")],
                    "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "months").endOf("month")],
                    "Last 90 Days": [moment().subtract(90, "days"), moment()],
                    "Year To Date": [moment().startOf("year"), moment()],
                  }}
                  format={"MM-DD-YYYY"}
                  onChange={onDateChange}
                  defaultValue={[moment().subtract(30, "days"), moment()]}
                  defaultPickerValue={[moment().subtract(30, "days"), moment()]}
                />
              </div>
            </div>
            {/* Bug 1840: Vendor(s)/Retailer(s) dropdowns removed from Global reports
                (expose vendor/retailer private data at the Global scope) - kept here
                commented out in case this filter is needed again in the future.
            {dealerMemberTypeName === "Admin" && (
              <>
                <div className="col-md-3">
                  <div className="input__block">
                    <label>{t("Vendor(s)")}</label>
                    <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Vendors")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                      {vendorList.map((item) => (
                        <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="input__block">
                    <label>{t("Retailer(s)")}</label>
                    <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                      {retailerList.map((item) => (
                        <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </>
            )}
            {dealerMemberTypeName === "Vendor" && (
              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Retailer(s)")}</label>
                  <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                    {retailerList.map((item) => (
                      <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
            {dealerMemberTypeName !== "Admin" && dealerMemberTypeName !== "Vendor" && (
              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Vendor(s)")}</label>
                  <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more vendor")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                    {vendorList.map((item) => (
                      <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
            */}
          </div>
        </div>

        <div className="col-md-12">
          <div className="edr-kpis">
            {GLOBAL_REPORT_PERCENT_ONLY ? (
              // Percent-only (Task 2344): the total would always be 100%, so the
              // card shows the change vs the prior period instead.
              <div className="edr-kpi">
                <div className="edr-kpi-lab">{t("Total Clicks")}</div>
                <div className={`edr-kpi-val ${chartsLoading ? "" : totalPct.valClass}`}>{chartsLoading ? "…" : totalPct.val}</div>
              </div>
            ) : (
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Total Clicks")}</div>
              <div className="edr-kpi-val">{chartsLoading ? "—" : totalClicks.toLocaleString()}</div>
              {clicksTrend && (
                <div className={`edr-kpi-trend ${clicksTrend.up ? "edr-up" : "edr-down"}`}>
                  {clicksTrend.up ? "▲" : "▼"} {Math.abs(clicksTrend.change).toFixed(2)}% {t("vs prior period")}
                </div>
              )}
            </div>
            )}
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top Category")}</div>
              <div className="edr-kpi-val">{topCategory?.category ?? "—"}</div>
              {topCategory && <div className="edr-kpi-sub">{GLOBAL_REPORT_PERCENT_ONLY ? <>{shareOf(topCategory.count, categoryData, "count")}% {t("of clicks")}</> : <>{Number(topCategory.count).toLocaleString()} {t("clicks")}</>}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top Price")}</div>
              <div className="edr-kpi-val">{topPriceBand?.label ?? "—"}</div>
              {topPriceBand && <div className="edr-kpi-sub">{GLOBAL_REPORT_PERCENT_ONLY ? <>{topPriceBand.percentage}% {t("of clicks")}</> : <>{topPriceBand.value.toLocaleString()} {t("clicks")}</>}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top Vendor")}</div>
              <div className="edr-kpi-val">{topVendor?.dealerCompany ?? "—"}</div>
              {topVendor && <div className="edr-kpi-sub">{GLOBAL_REPORT_PERCENT_ONLY ? <>{shareOf(topVendor.total, vendorClicksData, "total")}% {t("of clicks")}</> : <>{Number(topVendor.total).toLocaleString()} {t("clicks")}</>}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top Retailer")}</div>
              <div className="edr-kpi-val">{topRetailer?.dealerCompany ?? "—"}</div>
              {topRetailer && <div className="edr-kpi-sub">{GLOBAL_REPORT_PERCENT_ONLY ? <>{shareOf(topRetailer.total, retailerClicksData, "total")}% {t("of clicks")}</> : <>{Number(topRetailer.total).toLocaleString()} {t("clicks")}</>}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top Metal Type")}</div>
              <div className="edr-kpi-val">{chartsLoading ? "—" : topMetal?.metalType ?? "—"}</div>
              {!chartsLoading && topMetal && metalTotal > 0 && (
                <div className="edr-kpi-sub">{GLOBAL_REPORT_PERCENT_ONLY ? <>{((Number(topMetal.count) / metalTotal) * 100).toFixed(2)}% {t("of clicks")}</> : <>{Number(topMetal.count).toLocaleString()} · {((Number(topMetal.count) / metalTotal) * 100).toFixed(2)}%</>}</div>
              )}
            </div>
          </div>
        </div>

        {topCategory && (
          <div className="col-md-12">
            <div className="edr-insight">
              💡 {t("Demand profile this period")}: {t("shoppers gravitate to")} <b>{topCategory.category}</b>
              {topPriceBand && <> {t("in the")} <b>{topPriceBand.label}</b> {t("band")}</>}
              {topVendor && <>, {t("with")} <b>{topVendor.dealerCompany}</b> {t("the standout vendor")}</>}
              {topRetailer && <> {t("and")} <b>{topRetailer.dealerCompany}</b> {t("the standout retailer")}</>}.
            </div>
          </div>
        )}

        <div className="col-md-12" id="pdf-content">
          <div className="row">
            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Category")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && categoryChart.chartData.length > 1 ? (
                    <>
                      <CustomLegend chartData={categoryChart.chartData} colorData={categoryChart.colors} />
                      <div className="edr-chartwrap edr-tall">
                        <Doughnut
                          ref={categoryChartRef}
                          data={{
                            labels: categoryChart.chartData.slice(1).map((r) => r[0]),
                            datasets: [{ data: (GLOBAL_REPORT_PERCENT_ONLY ? toShareRows(categoryChart.chartData.slice(1)) : categoryChart.chartData.slice(1)).map((r) => r[1]), backgroundColor: categoryChart.colors, borderColor: "#fff", borderWidth: 2 }],
                          }}
                          options={GLOBAL_REPORT_PERCENT_ONLY ? donutPctOptions : donutOptions}
                          onClick={makeClickHandler(categoryChartRef, categoryChart.chartData.slice(1), handleCategoryClick)}
                        />
                      </div>
                    </>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Price")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && priceRows.length > 0 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar
                        ref={priceChartRef}
                        data={{
                          labels: priceChartData.slice(1).map((r) => r[0]),
                          datasets: [{ data: GLOBAL_REPORT_PERCENT_ONLY ? priceRows.map((r) => Number(r.percentage)) : priceChartData.slice(1).map((r) => r[1]), backgroundColor: priceChartData.slice(1).map((_, i) => chartColors[i % chartColors.length]), borderRadius: 4 }],
                        }}
                        options={(GLOBAL_REPORT_PERCENT_ONLY ? barPctOptions(false) : barOptions(false))}
                        onClick={makeClickHandler(priceChartRef, priceChartData.slice(1), handlePriceClick)}
                      />
                    </div>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Vendor")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && vendorChart.chartData.length > 1 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar
                        ref={vendorChartRef}
                        data={{
                          labels: vendorChart.chartData.slice(1).map((r) => r[0]),
                          datasets: [{ data: (GLOBAL_REPORT_PERCENT_ONLY ? toShareRows(vendorChart.chartData.slice(1)) : vendorChart.chartData.slice(1)).map((r) => r[1]), backgroundColor: vendorChart.colors, borderRadius: 4 }],
                        }}
                        options={(GLOBAL_REPORT_PERCENT_ONLY ? barPctOptions(true) : barOptions(true))}
                        onClick={makeClickHandler(vendorChartRef, vendorChart.chartData.slice(1), handleVendorClick)}
                      />
                    </div>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Retailer")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && retailerChart.chartData.length > 1 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar
                        ref={retailerChartRef}
                        data={{
                          labels: retailerChart.chartData.slice(1).map((r) => r[0]),
                          datasets: [{ data: (GLOBAL_REPORT_PERCENT_ONLY ? toShareRows(retailerChart.chartData.slice(1)) : retailerChart.chartData.slice(1)).map((r) => r[1]), backgroundColor: retailerChart.colors, borderRadius: 4 }],
                        }}
                        options={(GLOBAL_REPORT_PERCENT_ONLY ? barPctOptions(true) : barOptions(true))}
                        onClick={makeClickHandler(retailerChartRef, retailerChart.chartData.slice(1), handleRetailerClick)}
                      />
                    </div>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Metal Type Clicks")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && metalChart.chartData.length > 1 ? (
                    <>
                      <CustomLegend chartData={metalChart.chartData} colorData={metalChart.colors} />
                      <div className="edr-chartwrap edr-tall">
                        <Doughnut
                          ref={metalChartRef}
                          data={{
                            labels: metalChart.chartData.slice(1).map((r) => r[0]),
                            datasets: [{ data: (GLOBAL_REPORT_PERCENT_ONLY ? toShareRows(metalChart.chartData.slice(1)) : metalChart.chartData.slice(1)).map((r) => r[1]), backgroundColor: metalChart.colors, borderColor: "#fff", borderWidth: 2 }],
                          }}
                          options={GLOBAL_REPORT_PERCENT_ONLY ? donutPctOptions : donutOptions}
                          onClick={makeClickHandler(metalChartRef, metalChart.chartData.slice(1), handleMetalClick)}
                        />
                      </div>
                    </>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <JewelryDetailSearchTable
            dealerId={dealerId}
            forDealer={forDealer}
            dataRangeFrom={dataRangeFrom}
            dataRangeTo={dataRangeTo}
            vendorIds={vendorIds}
            retailerIds={retailerIds}
            dealerMembershipType={dealerMemberTypeName}
            filterParamName={filter.category ? "category" : filter.vendor ? "vendor" : filter.retailer ? "retailer" : filter.metalType !== "" ? "metalType" : "price"}
            filterValue={filter.category || filter.vendor || filter.retailer || filter.price}
            metalTypeId={filter.metalType}
            metalColorId={filter.metalColorType}
            price1={filter.price1}
            price2={filter.price2}
            fileNamePart="GlobalJewelryChartReports"
            dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
            refetchToken={refetchToken}
            reportType="Global"
            hideRowCount={GLOBAL_REPORT_PERCENT_ONLY}
            title={t("Product Click Log")}
          />
        </div>
      </div>
    </div>
  );
};

export default GlobalJewelryChartReports;
